import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MonthSelect } from "@/components/ui/month-select";
import {
  Clock,
  Play,
  Plus,
  Search,
  Settings,
  ArrowDownToLine,
  History,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { JobApi } from "@/api/job.api";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";

// Define types for jobs
interface Job {
  id: string;
  name: string;
  description: string;
  schedule: string;
  lastRun: string | null;
  nextRun: string | null;
  status: "active" | "inactive";
  isRunning: boolean;
}

interface JobHistory {
  id: string;
  jobId: string;
  jobName: string;
  startTime: string;
  endTime: string | null;
  status: "success" | "failed" | "running";
  logs: string;
}

// Sample data
const initialJobs: Job[] = [
  // {
  //   id: "1",
  //   name: "Daily Data Backup",
  //   description: "Creates a full backup of all system data",
  //   schedule: "0 0 * * *", // Cron expression (midnight every day)
  //   lastRun: "2025-05-07T00:00:00Z",
  //   nextRun: "2025-05-08T00:00:00Z",
  //   status: "active",
  //   isRunning: false,
  // },
  // {
  //   id: "2",
  //   name: "Weekly Report Generation",
  //   description: "Generates weekly summary reports for all programs",
  //   schedule: "0 9 * * 1", // 9:00 AM every Monday
  //   lastRun: "2025-05-06T09:00:00Z",
  //   nextRun: "2025-05-13T09:00:00Z",
  //   status: "active",
  //   isRunning: false,
  // },
  // {
  //   id: "3",
  //   name: "Monthly Invoice Processing",
  //   description: "Processes all pending invoices for the month",
  //   schedule: "0 12 1 * *", // Noon on the 1st day of every month
  //   lastRun: "2025-05-01T12:00:00Z",
  //   nextRun: "2025-06-01T12:00:00Z",
  //   status: "active",
  //   isRunning: false,
  // },
  // {
  //   id: "4",
  //   name: "Database Optimization",
  //   description: "Optimizes database indexes and performs cleanup",
  //   schedule: "0 2 * * 0", // 2:00 AM every Sunday
  //   lastRun: "2025-05-05T02:00:00Z",
  //   nextRun: "2025-05-12T02:00:00Z",
  //   status: "inactive",
  //   isRunning: false,
  // },
  {
    id: "5",
    name: "Fetch latest FX rates",
    description:
      "Fetches the latest foreign exchange rates from external providers.",
    schedule: "0 * * * *", // Every hour
    lastRun: null,
    nextRun: null,
    status: "active",
    isRunning: false,
  },
  {
    id: "6",
    name: "Generate partner invoice",
    description:
      "Generates invoices for a given partner for a given month based on transactions.",
    schedule: "0 3 * * 1", // 3:00 AM every Monday
    lastRun: null,
    nextRun: null,
    status: "active",
    isRunning: false,
  },
  {
    id: "7",
    name: "Generate client invoice",
    description:
      "Generates invoices for a given client for a given month based on transactions.",
    schedule: "0 4 * * 1", // 4:00 AM every Monday
    lastRun: null,
    nextRun: null,
    status: "active",
    isRunning: false,
  },
  {
    id: "8",
    name: "Generate program invoice",
    description:
      "Generates invoices for a given program for a given month based on transactions..",
    schedule: "0 5 * * 1", // 5:00 AM every Monday
    lastRun: null,
    nextRun: null,
    status: "active",
    isRunning: false,
  },
  {
    id: "9",
    name: "Generate merchant invoice",
    description:
      "Generates invoices for a given merchant for a given month based on transactions.",
    schedule: "0 6 * * 1", // 6:00 AM every Monday
    lastRun: null,
    nextRun: null,
    status: "active",
    isRunning: false,
  },
  {
    id: "10",
    name: "Process accruals",
    description: "Processes accruals for all relevant merchants and programs.",
    schedule: "30 1 * * *", // 1:30 AM every day
    lastRun: null,
    nextRun: null,
    status: "active",
    isRunning: false,
  },
];

const initialJobHistory: JobHistory[] = [
  {
    id: "1",
    jobId: "1",
    jobName: "Daily Data Backup",
    startTime: "2025-05-07T00:00:00Z",
    endTime: "2025-05-07T00:05:23Z",
    status: "success",
    logs: "Started backup at 2025-05-07T00:00:00Z\nBacked up 1.2GB of data\nCompleted successfully at 2025-05-07T00:05:23Z",
  },
  {
    id: "2",
    jobId: "1",
    jobName: "Daily Data Backup",
    startTime: "2025-05-06T00:00:00Z",
    endTime: "2025-05-06T00:04:45Z",
    status: "success",
    logs: "Started backup at 2025-05-06T00:00:00Z\nBacked up 1.1GB of data\nCompleted successfully at 2025-05-06T00:04:45Z",
  },
  {
    id: "3",
    jobId: "2",
    jobName: "Weekly Report Generation",
    startTime: "2025-05-06T09:00:00Z",
    endTime: "2025-05-06T09:10:12Z",
    status: "success",
    logs: "Started report generation at 2025-05-06T09:00:00Z\nGenerated 15 reports\nCompleted successfully at 2025-05-06T09:10:12Z",
  },
  {
    id: "4",
    jobId: "3",
    jobName: "Monthly Invoice Processing",
    startTime: "2025-05-01T12:00:00Z",
    endTime: "2025-05-01T12:15:30Z",
    status: "success",
    logs: "Started invoice processing at 2025-05-01T12:00:00Z\nProcessed 142 invoices\nCompleted successfully at 2025-05-01T12:15:30Z",
  },
  {
    id: "5",
    jobId: "4",
    jobName: "Database Optimization",
    startTime: "2025-05-05T02:00:00Z",
    endTime: null,
    status: "failed",
    logs: "Started optimization at 2025-05-05T02:00:00Z\nError: Connection timeout at 2025-05-05T02:03:42Z\nProcess terminated",
  },
];

// Custom styling for badge variants
const getStatusVariant = (
  status: "active" | "inactive" | "success" | "failed" | "running"
): "default" | "destructive" | "outline" | "secondary" | "success" => {
  switch (status) {
    case "active":
    case "success":
      return "success"; // Using success badge variant
    case "inactive":
      return "secondary";
    case "failed":
      return "destructive";
    case "running":
      return "outline";
    default:
      return "secondary";
  }
};

const Jobs = () => {
  const [jobs, setJobs] = useState<Job[]>(initialJobs);
  const [jobHistory, setJobHistory] = useState<JobHistory[]>(initialJobHistory);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedJob, setSelectedJob] = useState<Job | null>(() => {
    // Set FX rates job as default (id: "5")
    return initialJobs.find((job) => job.id === "5") || null;
  });
  const [selectedJobHistory, setSelectedJobHistory] =
    useState<JobHistory | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPartnerInvoiceDialogOpen, setIsPartnerInvoiceDialogOpen] =
    useState(false);
  const [isClientInvoiceDialogOpen, setIsClientInvoiceDialogOpen] =
    useState(false);
  const [isProgramInvoiceDialogOpen, setIsProgramInvoiceDialogOpen] =
    useState(false);
  const [isMerchantInvoiceDialogOpen, setIsMerchantInvoiceDialogOpen] =
    useState(false);
  const [isAccrualJobDialogOpen, setIsAccrualJobDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Partner invoice form state
  const [partnerInvoiceForm, setPartnerInvoiceForm] = useState({
    transferPartnerCode: "",
    invoicePeriodMonth: "",
    invoicePeriodYear: new Date().getFullYear().toString(),
  });

  // Client invoice form state
  const [clientInvoiceForm, setClientInvoiceForm] = useState({
    clientId: "",
    invoicePeriodMonth: "",
    invoicePeriodYear: new Date().getFullYear().toString(),
  });

  // Program invoice form state
  const [programInvoiceForm, setProgramInvoiceForm] = useState({
    programCode: "",
    invoicePeriodMonth: "",
    invoicePeriodYear: new Date().getFullYear().toString(),
  });

  // Merchant invoice form state
  const [merchantInvoiceForm, setMerchantInvoiceForm] = useState({
    merchantCode: "",
    invoicePeriodMonth: "",
    invoicePeriodYear: new Date().getFullYear().toString(),
  });

  // Accrual job form state
  const [accrualJobForm, setAccrualJobForm] = useState({
    programCode: "",
    merchantCode: "",
  });
  const [activeTab, setActiveTab] = useState("jobs");

  // New job form state
  const [newJobName, setNewJobName] = useState("");
  const [newJobDescription, setNewJobDescription] = useState("");
  const [newJobSchedule, setNewJobSchedule] = useState("");
  const [newJobStatus, setNewJobStatus] = useState(true);

  // History pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalHistoryCount, setTotalHistoryCount] = useState(0);

  // Ensure currentPage is never less than 1, but allow it to be exactly 1
  const safeCurrentPage = currentPage < 1 ? 1 : currentPage;

  // Fetch history for specific job
  const fetchJobHistory = async (job: Job | null, page: number = 1) => {
    setLoading(true);

    try {
      if (
        job &&
        (job.name === "Fetch latest FX rates" ||
          job.name === "Generate partner invoice" ||
          job.name === "Generate client invoice" ||
          job.name === "Generate program invoice" ||
          job.name === "Generate merchant invoice" ||
          job.name === "Process accruals")
      ) {
        // Determine jobName based on selected job
        let jobNameForApi = "";
        if (job.name === "Fetch latest FX rates") {
          jobNameForApi = "fxRateJobDetail";
        } else if (job.name === "Generate partner invoice") {
          jobNameForApi = "generateTransferPartnerInvoice";
        } else if (job.name === "Generate client invoice") {
          jobNameForApi = "generateClientInvoice";
        } else if (job.name === "Generate program invoice") {
          jobNameForApi = "generateProgramInvoice";
        } else if (job.name === "Generate merchant invoice") {
          jobNameForApi = "generateMerchantInvoice";
        } else if (job.name === "Process accruals") {
          jobNameForApi = "processTransactionsJobETFinfareJobDetail";
        }

        // Call API with pagination parameters
        const payload = {
          jobName: jobNameForApi,
          startTime: null,
          endTime: null,
          pageNumber: page - 1, // Convert to 0-based index for API
          pageSize: rowsPerPage,
          sortBy: "id",
          sortOrder: "desc",
        };

        const res = await JobApi.fetchFxRateJobHistory(payload);
        const apiHistory = Array.isArray(res.data?.jobs) ? res.data.jobs : [];
        const totalCount = res.data?.totalCount || 0;

        // Map API response to JobHistory[]
        const mappedHistory = apiHistory.map((h: any) => {
          const mappedItem = {
            id: h.id?.toString() ?? "",
            jobId: h.id?.toString() ?? "", // Using id as jobId since API doesn't provide separate jobId
            jobName: h.jobName ?? job.name, // Use jobName from API response or fallback to selected job name
            startTime: h.startTime ?? "",
            endTime: h.endTime ?? "",
            status: h.status?.toLowerCase() ?? "success", // Convert to lowercase to match our enum
            logs: `Job executed from ${h.startTime} to ${h.endTime}. Duration: ${h.duration}ms. Status: ${h.status}`,
          };
          console.log("Mapped history item:", mappedItem);
          return mappedItem;
        });
        console.log("Setting job history with", mappedHistory.length, "items");
        setJobHistory(mappedHistory);
        setTotalHistoryCount(totalCount);
      } else {
        // For other jobs, show empty history for now
        setJobHistory([]);
        setTotalHistoryCount(0);
      }
    } catch (error) {
      console.error("Error fetching job history:", error);
      setJobHistory([]);
      setTotalHistoryCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch history when selected job changes, page changes, or switching to history tab
  useEffect(() => {
    if (activeTab === "history" && selectedJob) {
      console.log(
        "Fetching job history for:",
        selectedJob.name,
        "page:",
        currentPage
      );
      fetchJobHistory(selectedJob, currentPage);
    }
  }, [selectedJob, currentPage, rowsPerPage, activeTab]); // Added activeTab dependency

  // Reset pagination when switching to history tab
  useEffect(() => {
    if (activeTab === "history") {
      console.log("Switching to history tab - resetting pagination");
      setCurrentPage(1);
    }
  }, [activeTab]);

  // Monitor currentPage changes
  useEffect(() => {
    console.log("currentPage changed to:", currentPage);
  }, [currentPage]);

  // Filter jobs based on search query
  const filteredJobs = jobs.filter(
    (job) =>
      job.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter history based on search query and selected job
  const filteredHistory = jobHistory.filter((history) => {
    // For FX rates job, show all FX job history regardless of jobId matching
    const matchesSelectedJob =
      !selectedJob ||
      (selectedJob.name === "Fetch latest FX rates" &&
        history.jobName === "Fetch latest FX rates") ||
      history.jobId === selectedJob.id;
    const matchesSearchQuery = history.jobName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    console.log("Filtering history item:", {
      historyJobName: history.jobName,
      selectedJobName: selectedJob?.name,
      matchesSelectedJob,
      matchesSearchQuery,
      finalResult: matchesSelectedJob && matchesSearchQuery,
    });

    return matchesSelectedJob && matchesSearchQuery;
  });

  console.log("Filtered history count:", filteredHistory.length);
  console.log("Total job history count:", jobHistory.length);

  // Pagination for history (server-side)
  const totalPages = Math.ceil(totalHistoryCount / rowsPerPage);
  const currentHistoryItems = jobHistory; // Use the data directly from API since it's already paginated

  console.log("Pagination debug:", {
    currentPage,
    rowsPerPage,
    totalHistoryCount,
    currentHistoryItemsLength: currentHistoryItems.length,
    totalPages,
  });

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";

    try {
      return format(new Date(dateString), "MMM d, yyyy - h:mm a");
    } catch (error) {
      return "Invalid date";
    }
  };

  // Create new job
  const handleCreateJob = () => {
    if (!newJobName.trim()) {
      toast.error("Job name is required");
      return;
    }

    const newJob: Job = {
      id: `${jobs.length + 1}`,
      name: newJobName,
      description: newJobDescription,
      schedule: newJobSchedule,
      lastRun: null,
      nextRun: null,
      status: newJobStatus ? "active" : "inactive",
      isRunning: false,
    };

    setJobs([...jobs, newJob]);
    setNewJobName("");
    setNewJobDescription("");
    setNewJobSchedule("");
    setNewJobStatus(true);
    setIsCreateDialogOpen(false);
    toast.success("Job created successfully");
  };

  // Toggle job status
  const toggleJobStatus = (jobId: string) => {
    setJobs(
      jobs.map((job) =>
        job.id === jobId
          ? { ...job, status: job.status === "active" ? "inactive" : "active" }
          : job
      )
    );
    toast.success("Job status updated");
  };

  // Run job now
  const runJobNow = (job: Job) => {
    console.log("runJobNow called with job:", job.name); // Debug log

    if (job.isRunning) {
      toast.error("Job is already running");
      return;
    }

    // Special case: Generate partner invoice job
    if (job.name === "Generate partner invoice") {
      console.log("Opening partner invoice dialog"); // Debug log
      setIsPartnerInvoiceDialogOpen(true);
      return;
    }

    // Special case: Generate client invoice job
    if (job.name === "Generate client invoice") {
      console.log("Opening client invoice dialog"); // Debug log
      setIsClientInvoiceDialogOpen(true);
      return;
    }

    // Special case: Generate program invoice job
    if (job.name === "Generate program invoice") {
      console.log("Opening program invoice dialog"); // Debug log
      setIsProgramInvoiceDialogOpen(true);
      return;
    }

    // Special case: Generate merchant invoice job
    if (job.name === "Generate merchant invoice") {
      console.log("Opening merchant invoice dialog"); // Debug log
      setIsMerchantInvoiceDialogOpen(true);
      return;
    }

    // Special case: Process accruals job
    if (job.name === "Process accruals") {
      console.log("Opening accrual job dialog"); // Debug log
      setIsAccrualJobDialogOpen(true);
      return;
    }

    // Special case: Fetch latest FX rates job
    if (job.name === "Fetch latest FX rates") {
      // Update job to indicate it's running
      const updatedJobs = jobs.map((j) =>
        j.id === job.id ? { ...j, isRunning: true } : j
      );
      setJobs(updatedJobs);

      // Create a new history entry
      const startTime = new Date().toISOString();
      const newHistoryEntry: JobHistory = {
        id: `${jobHistory.length + 1}`,
        jobId: job.id,
        jobName: job.name,
        startTime,
        endTime: null,
        status: "running",
        logs: `Started job at ${startTime}\nFetching latest FX rates...`,
      };

      setJobHistory([newHistoryEntry, ...jobHistory]);

      toast.loading("Fetching latest FX rates...");

      JobApi.fetchFxRatesAndUpdateInDB()
        .then(() => {
          // Dismiss loading toast
          toast.dismiss();

          // Update job status
          const completedJobs = jobs.map((j) =>
            j.id === job.id
              ? {
                  ...j,
                  isRunning: false,
                  lastRun: new Date().toISOString(),
                  nextRun: j.nextRun,
                }
              : j
          );
          setJobs(completedJobs);

          // Update history entry
          const endTime = new Date().toISOString();
          const updatedHistory = jobHistory.map((h) =>
            h.id === newHistoryEntry.id
              ? {
                  ...h,
                  endTime,
                  status: "success" as const,
                  logs: `${h.logs}\nFX rates fetched and updated successfully\nCompleted at ${endTime}`,
                }
              : h
          );

          setJobHistory([newHistoryEntry, ...updatedHistory.slice(1)]);

          toast.success("FX rates fetched and updated successfully");
        })
        .catch((error) => {
          // Dismiss loading toast
          toast.dismiss();

          // Update job status
          const completedJobs = jobs.map((j) =>
            j.id === job.id
              ? {
                  ...j,
                  isRunning: false,
                  lastRun: new Date().toISOString(),
                  nextRun: j.nextRun,
                }
              : j
          );
          setJobs(completedJobs);

          // Update history entry
          const endTime = new Date().toISOString();
          const updatedHistory = jobHistory.map((h) =>
            h.id === newHistoryEntry.id
              ? {
                  ...h,
                  endTime,
                  status: "failed" as const,
                  logs: `${h.logs}\nError: Failed to fetch FX rates\nTerminated at ${endTime}`,
                }
              : h
          );

          setJobHistory([newHistoryEntry, ...updatedHistory.slice(1)]);

          toast.error("Failed to fetch FX rates");
        });

      return; // Exit early for FX rates job
    }

    // Generic job handling for other jobs
    // Update job to indicate it's running
    const updatedJobs = jobs.map((j) =>
      j.id === job.id ? { ...j, isRunning: true } : j
    );
    setJobs(updatedJobs);

    // Create a new history entry
    const startTime = new Date().toISOString();
    const newHistoryEntry: JobHistory = {
      id: `${jobHistory.length + 1}`,
      jobId: job.id,
      jobName: job.name,
      startTime,
      endTime: null,
      status: "running", // Explicitly using the literal "running" from the union type
      logs: `Started job at ${startTime}\nRunning...`,
    };

    // setJobHistory([newHistoryEntry, ...jobHistory])

    toast.success(`Running job: ${job.name}`);

    // Simulate job completion after a delay
    setTimeout(() => {
      // Update job status
      const completedJobs = jobs.map((j) =>
        j.id === job.id
          ? {
              ...j,
              isRunning: false,
              lastRun: new Date().toISOString(),
              nextRun: j.nextRun, // Would normally be recalculated based on schedule
            }
          : j
      );
      setJobs(completedJobs);

      // Update history entry
      const endTime = new Date().toISOString();
      const success = Math.random() > 0.2; // 80% success rate for simulation

      // Fix: Ensure we're using the proper type for status
      const jobStatus: "success" | "failed" = success ? "success" : "failed";

      const updatedHistory = jobHistory.map((h) =>
        h.id === newHistoryEntry.id
          ? {
              ...h,
              endTime,
              status: jobStatus, // Using the properly typed status
              logs: success
                ? `${h.logs}\nProcessed data successfully\nCompleted at ${endTime}`
                : `${h.logs}\nError: Operation failed\nTerminated at ${endTime}`,
            }
          : h
      );

      setJobHistory([newHistoryEntry, ...updatedHistory.slice(1)]);

      if (success) {
        toast.success(`Job completed: ${job.name}`);
      } else {
        toast.error(`Job failed: ${job.name}`);
      }
    }, 3000);
  };

  // View job history details
  const viewJobHistoryDetails = (history: JobHistory) => {
    setSelectedJobHistory(history);
  };

  // Handle partner invoice form submission
  const handlePartnerInvoiceSubmit = () => {
    // Validate form data
    if (!partnerInvoiceForm.transferPartnerCode.trim()) {
      toast.error("Please enter Transfer Partner Code");
      return;
    }
    if (!partnerInvoiceForm.invoicePeriodMonth) {
      toast.error("Please select Invoice Period Month");
      return;
    }
    if (!partnerInvoiceForm.invoicePeriodYear.trim()) {
      toast.error("Please enter Invoice Period Year");
      return;
    }

    // Show loading toast
    toast.loading("Generating partner invoice...");

    // Call the API
    JobApi.generateTransferPartnerInvoice(
      partnerInvoiceForm.transferPartnerCode,
      partnerInvoiceForm.invoicePeriodMonth,
      partnerInvoiceForm.invoicePeriodYear
    )
      .then((response) => {
        // Dismiss loading toast
        toast.dismiss();

        // Close the dialog
        setIsPartnerInvoiceDialogOpen(false);

        // Reset form
        setPartnerInvoiceForm({
          transferPartnerCode: "",
          invoicePeriodMonth: "",
          invoicePeriodYear: new Date().getFullYear().toString(),
        });

        // Show success message
        toast.success("Partner invoice generated successfully");

        console.log("API Response:", response);
      })
      .catch((error) => {
        // Dismiss loading toast
        toast.dismiss();

        // Show error message
        toast.error("Failed to generate partner invoice");

        console.error("API Error:", error);
      });
  };

  // Handle partner invoice form cancellation
  const handlePartnerInvoiceCancel = () => {
    setIsPartnerInvoiceDialogOpen(false);

    // Reset form
    setPartnerInvoiceForm({
      transferPartnerCode: "",
      invoicePeriodMonth: "",
      invoicePeriodYear: new Date().getFullYear().toString(),
    });
  };

  // Handle client invoice form submission
  const handleClientInvoiceSubmit = () => {
    // Validate form data
    if (!clientInvoiceForm.clientId.trim()) {
      toast.error("Please enter Client ID");
      return;
    }
    if (!clientInvoiceForm.invoicePeriodMonth) {
      toast.error("Please select Invoice Period Month");
      return;
    }
    if (!clientInvoiceForm.invoicePeriodYear.trim()) {
      toast.error("Please enter Invoice Period Year");
      return;
    }

    // Show loading toast
    toast.loading("Generating client invoice...");

    // Call the API
    JobApi.generateClientInvoice(
      clientInvoiceForm.clientId,
      clientInvoiceForm.invoicePeriodMonth,
      clientInvoiceForm.invoicePeriodYear
    )
      .then((response) => {
        // Dismiss loading toast
        toast.dismiss();

        // Close the dialog
        setIsClientInvoiceDialogOpen(false);

        // Reset form
        setClientInvoiceForm({
          clientId: "",
          invoicePeriodMonth: "",
          invoicePeriodYear: new Date().getFullYear().toString(),
        });

        // Show success message
        toast.success("Client invoice generated successfully");

        console.log("API Response:", response);
      })
      .catch((error) => {
        // Dismiss loading toast
        toast.dismiss();

        // Show error message
        toast.error("Failed to generate client invoice");

        console.error("API Error:", error);
      });
  };

  // Handle client invoice form cancellation
  const handleClientInvoiceCancel = () => {
    setIsClientInvoiceDialogOpen(false);

    // Reset form
    setClientInvoiceForm({
      clientId: "",
      invoicePeriodMonth: "",
      invoicePeriodYear: new Date().getFullYear().toString(),
    });
  };

  // Handle program invoice form submission
  const handleProgramInvoiceSubmit = () => {
    // Validate form data
    if (!programInvoiceForm.programCode.trim()) {
      toast.error("Please enter Program Code");
      return;
    }
    if (!programInvoiceForm.invoicePeriodMonth) {
      toast.error("Please select Invoice Period Month");
      return;
    }
    if (!programInvoiceForm.invoicePeriodYear.trim()) {
      toast.error("Please enter Invoice Period Year");
      return;
    }

    // Show loading toast
    toast.loading("Generating program invoice...");

    // Call the API
    JobApi.generateProgramInvoice(
      programInvoiceForm.programCode,
      programInvoiceForm.invoicePeriodMonth,
      programInvoiceForm.invoicePeriodYear
    )
      .then((response) => {
        // Dismiss loading toast
        toast.dismiss();

        // Close the dialog
        setIsProgramInvoiceDialogOpen(false);

        // Reset form
        setProgramInvoiceForm({
          programCode: "",
          invoicePeriodMonth: "",
          invoicePeriodYear: new Date().getFullYear().toString(),
        });

        // Show success message
        toast.success("Program invoice generated successfully");

        console.log("API Response:", response);
      })
      .catch((error) => {
        // Dismiss loading toast
        toast.dismiss();

        // Show error message
        toast.error("Failed to generate program invoice");

        console.error("API Error:", error);
      });
  };

  // Handle program invoice form cancellation
  const handleProgramInvoiceCancel = () => {
    setIsProgramInvoiceDialogOpen(false);

    // Reset form
    setProgramInvoiceForm({
      programCode: "",
      invoicePeriodMonth: "",
      invoicePeriodYear: new Date().getFullYear().toString(),
    });
  };

  // Handle merchant invoice form submission
  const handleMerchantInvoiceSubmit = () => {
    // Validate form data
    if (!merchantInvoiceForm.merchantCode.trim()) {
      toast.error("Please enter Merchant Code");
      return;
    }
    if (!merchantInvoiceForm.invoicePeriodMonth) {
      toast.error("Please select Invoice Period Month");
      return;
    }
    if (!merchantInvoiceForm.invoicePeriodYear.trim()) {
      toast.error("Please enter Invoice Period Year");
      return;
    }

    // Show loading toast
    toast.loading("Generating merchant invoice...");

    // Call the API
    JobApi.generateMerchantInvoice(
      merchantInvoiceForm.merchantCode,
      merchantInvoiceForm.invoicePeriodMonth,
      merchantInvoiceForm.invoicePeriodYear
    )
      .then((response) => {
        // Dismiss loading toast
        toast.dismiss();

        // Close the dialog
        setIsMerchantInvoiceDialogOpen(false);

        // Reset form
        setMerchantInvoiceForm({
          merchantCode: "",
          invoicePeriodMonth: "",
          invoicePeriodYear: new Date().getFullYear().toString(),
        });

        // Show success message
        toast.success("Merchant invoice generated successfully");

        console.log("API Response:", response);
      })
      .catch((error) => {
        // Dismiss loading toast
        toast.dismiss();

        // Show error message
        toast.error("Failed to generate merchant invoice");

        console.error("API Error:", error);
      });
  };

  // Handle merchant invoice form cancellation
  const handleMerchantInvoiceCancel = () => {
    setIsMerchantInvoiceDialogOpen(false);

    // Reset form
    setMerchantInvoiceForm({
      merchantCode: "",
      invoicePeriodMonth: "",
      invoicePeriodYear: new Date().getFullYear().toString(),
    });
  };

  // Handle accrual job form submission
  const handleAccrualJobSubmit = () => {
    // Validate form data
    if (!accrualJobForm.programCode.trim()) {
      toast.error("Please enter Program Code");
      return;
    }
    if (!accrualJobForm.merchantCode.trim()) {
      toast.error("Please enter Merchant Code");
      return;
    }

    // Show loading toast
    toast.loading("Processing accruals...");

    // Call the API
    JobApi.processAccruals(
      accrualJobForm.merchantCode,
      accrualJobForm.programCode
    )
      .then((response) => {
        // Dismiss loading toast
        toast.dismiss();

        // Close the dialog
        setIsAccrualJobDialogOpen(false);

        // Reset form
        setAccrualJobForm({
          programCode: "",
          merchantCode: "",
        });

        // Show success message
        toast.success("Accruals processed successfully");

        console.log("API Response:", response);
      })
      .catch((error) => {
        // Dismiss loading toast
        toast.dismiss();

        // Show error message
        toast.error("Failed to process accruals");

        console.error("API Error:", error);
      });
  };

  // Handle accrual job form cancellation
  const handleAccrualJobCancel = () => {
    setIsAccrualJobDialogOpen(false);

    // Reset form
    setAccrualJobForm({
      programCode: "",
      merchantCode: "",
    });
  };

  return (
    <div className="space-y-6">
      {/* Partner Invoice Dialog - Moved to top level */}
      <Dialog
        open={isPartnerInvoiceDialogOpen}
        onOpenChange={(open) => {
          console.log("Dialog onOpenChange:", open); // Debug log
          setIsPartnerInvoiceDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invoicing - Transfer Partner Job</DialogTitle>
            <DialogDescription>
              Configure parameters for partner invoice generation
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div>
              <Label htmlFor="transferPartnerCode" className="mb-2 block">
                Transfer Partner Code
              </Label>
              <Input
                id="transferPartnerCode"
                placeholder="Enter transfer partner code"
                value={partnerInvoiceForm.transferPartnerCode}
                onChange={(e) =>
                  setPartnerInvoiceForm({
                    ...partnerInvoiceForm,
                    transferPartnerCode: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="invoicePeriodMonth" className="mb-2 block">
                Invoice Period Month
              </Label>
              <MonthSelect
                value={partnerInvoiceForm.invoicePeriodMonth}
                onChange={(value) =>
                  setPartnerInvoiceForm({
                    ...partnerInvoiceForm,
                    invoicePeriodMonth: value,
                  })
                }
                placeholder="Select month"
              />
            </div>
            <div>
              <Label htmlFor="invoicePeriodYear" className="mb-2 block">
                Invoice Period Year
              </Label>
              <Input
                id="invoicePeriodYear"
                type="number"
                placeholder="Enter year"
                value={partnerInvoiceForm.invoicePeriodYear}
                onChange={(e) =>
                  setPartnerInvoiceForm({
                    ...partnerInvoiceForm,
                    invoicePeriodYear: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handlePartnerInvoiceCancel}>
              Cancel
            </Button>
            <Button onClick={handlePartnerInvoiceSubmit}>Run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Invoice Dialog */}
      <Dialog
        open={isClientInvoiceDialogOpen}
        onOpenChange={(open) => {
          console.log("Client Dialog onOpenChange:", open); // Debug log
          setIsClientInvoiceDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invoicing - Client Invoice Job</DialogTitle>
            <DialogDescription>
              Configure parameters for client invoice generation
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div>
              <Label htmlFor="clientId" className="mb-2 block">
                Client ID
              </Label>
              <Input
                id="clientId"
                placeholder="Enter client ID"
                value={clientInvoiceForm.clientId}
                onChange={(e) =>
                  setClientInvoiceForm({
                    ...clientInvoiceForm,
                    clientId: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="clientInvoicePeriodMonth" className="mb-2 block">
                Invoice Period Month
              </Label>
              <MonthSelect
                value={clientInvoiceForm.invoicePeriodMonth}
                onChange={(value) =>
                  setClientInvoiceForm({
                    ...clientInvoiceForm,
                    invoicePeriodMonth: value,
                  })
                }
                placeholder="Select month"
              />
            </div>
            <div>
              <Label htmlFor="clientInvoicePeriodYear" className="mb-2 block">
                Invoice Period Year
              </Label>
              <Input
                id="clientInvoicePeriodYear"
                type="number"
                placeholder="Enter year"
                value={clientInvoiceForm.invoicePeriodYear}
                onChange={(e) =>
                  setClientInvoiceForm({
                    ...clientInvoiceForm,
                    invoicePeriodYear: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClientInvoiceCancel}>
              Cancel
            </Button>
            <Button onClick={handleClientInvoiceSubmit}>Run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Program Invoice Dialog */}
      <Dialog
        open={isProgramInvoiceDialogOpen}
        onOpenChange={(open) => {
          console.log("Program Dialog onOpenChange:", open); // Debug log
          setIsProgramInvoiceDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invoicing - Program Job</DialogTitle>
            <DialogDescription>
              Configure parameters for program invoice generation
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div>
              <Label htmlFor="programCode" className="mb-2 block">
                Program Code
              </Label>
              <Input
                id="programCode"
                placeholder="Enter program code"
                value={programInvoiceForm.programCode}
                onChange={(e) =>
                  setProgramInvoiceForm({
                    ...programInvoiceForm,
                    programCode: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="programInvoicePeriodMonth" className="mb-2 block">
                Invoice Period Month
              </Label>
              <MonthSelect
                value={programInvoiceForm.invoicePeriodMonth}
                onChange={(value) =>
                  setProgramInvoiceForm({
                    ...programInvoiceForm,
                    invoicePeriodMonth: value,
                  })
                }
                placeholder="Select month"
              />
            </div>
            <div>
              <Label htmlFor="programInvoicePeriodYear" className="mb-2 block">
                Invoice Period Year
              </Label>
              <Input
                id="programInvoicePeriodYear"
                type="number"
                placeholder="Enter year"
                value={programInvoiceForm.invoicePeriodYear}
                onChange={(e) =>
                  setProgramInvoiceForm({
                    ...programInvoiceForm,
                    invoicePeriodYear: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleProgramInvoiceCancel}>
              Cancel
            </Button>
            <Button onClick={handleProgramInvoiceSubmit}>Run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merchant Invoice Dialog */}
      <Dialog
        open={isMerchantInvoiceDialogOpen}
        onOpenChange={(open) => {
          console.log("Merchant Dialog onOpenChange:", open); // Debug log
          setIsMerchantInvoiceDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invoicing - Earn Merchant Job</DialogTitle>
            <DialogDescription>
              Configure parameters for merchant invoice generation
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div>
              <Label htmlFor="merchantCode" className="mb-2 block">
                Merchant Code
              </Label>
              <Input
                id="merchantCode"
                placeholder="Enter merchant code"
                value={merchantInvoiceForm.merchantCode}
                onChange={(e) =>
                  setMerchantInvoiceForm({
                    ...merchantInvoiceForm,
                    merchantCode: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label
                htmlFor="merchantInvoicePeriodMonth"
                className="mb-2 block"
              >
                Invoice Period Month
              </Label>
              <MonthSelect
                value={merchantInvoiceForm.invoicePeriodMonth}
                onChange={(value) =>
                  setMerchantInvoiceForm({
                    ...merchantInvoiceForm,
                    invoicePeriodMonth: value,
                  })
                }
                placeholder="Select month"
              />
            </div>
            <div>
              <Label htmlFor="merchantInvoicePeriodYear" className="mb-2 block">
                Invoice Period Year
              </Label>
              <Input
                id="merchantInvoicePeriodYear"
                type="number"
                placeholder="Enter year"
                value={merchantInvoiceForm.invoicePeriodYear}
                onChange={(e) =>
                  setMerchantInvoiceForm({
                    ...merchantInvoiceForm,
                    invoicePeriodYear: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleMerchantInvoiceCancel}>
              Cancel
            </Button>
            <Button onClick={handleMerchantInvoiceSubmit}>Run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accrual Job Dialog */}
      <Dialog
        open={isAccrualJobDialogOpen}
        onOpenChange={(open) => {
          console.log("Accrual Dialog onOpenChange:", open); // Debug log
          setIsAccrualJobDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Accrual Job</DialogTitle>
            <DialogDescription>
              Configure parameters for processing accruals
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div>
              <Label htmlFor="accrualProgramCode" className="mb-2 block">
                Program Code
              </Label>
              <Input
                id="accrualProgramCode"
                placeholder="Enter program code"
                value={accrualJobForm.programCode}
                onChange={(e) =>
                  setAccrualJobForm({
                    ...accrualJobForm,
                    programCode: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label htmlFor="accrualMerchantCode" className="mb-2 block">
                Merchant Code
              </Label>
              <Input
                id="accrualMerchantCode"
                placeholder="Enter merchant code"
                value={accrualJobForm.merchantCode}
                onChange={(e) =>
                  setAccrualJobForm({
                    ...accrualJobForm,
                    merchantCode: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleAccrualJobCancel}>
              Cancel
            </Button>
            <Button onClick={handleAccrualJobSubmit}>Run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-semibold">Job Management</h1>
        <div className="flex items-center gap-2">
          <Dialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
          >
            {/* <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus size={16} /> Add New Job
              </Button>
            </DialogTrigger> */}
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Job</DialogTitle>
                <DialogDescription>
                  Configure a new scheduled job for the system
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="job-name">Job Name</Label>
                  <Input
                    id="job-name"
                    placeholder="e.g., Daily Backup"
                    value={newJobName}
                    onChange={(e) => setNewJobName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job-description">Description</Label>
                  <Textarea
                    id="job-description"
                    placeholder="What does this job do?"
                    value={newJobDescription}
                    onChange={(e) => setNewJobDescription(e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job-schedule">Schedule (CRON)</Label>
                  <Input
                    id="job-schedule"
                    placeholder="e.g., 0 0 * * *"
                    value={newJobSchedule}
                    onChange={(e) => setNewJobSchedule(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    Format: minute hour day-of-month month day-of-week
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={newJobStatus}
                    onCheckedChange={setNewJobStatus}
                    id="job-status"
                  />
                  <Label htmlFor="job-status">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreateJob}>Create Job</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative mb-6">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          size={18}
        />
        <Input
          placeholder="Search Jobs"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="jobs" className="flex items-center gap-2">
            <Settings size={16} /> Jobs
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History size={16} /> Job History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Jobs</CardTitle>
              <CardDescription>
                Configure and manage automated system jobs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center space-x-4 p-4 border rounded-lg"
                    >
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-6 w-48" />
                        <Skeleton className="h-4 w-72" />
                        <div className="flex gap-4 mt-2">
                          <Skeleton className="h-4 w-40" />
                          <Skeleton className="h-4 w-40" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Skeleton className="h-9 w-20 rounded-md" />
                        <Skeleton className="h-9 w-20 rounded-md" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredJobs.length === 0 ? (
                    <div className="text-center p-8 text-gray-500">
                      No jobs found
                    </div>
                  ) : (
                    filteredJobs.map((job) => (
                      <div
                        key={job.id}
                        className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1 mb-4 md:mb-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-lg">{job.name}</h3>
                            <Badge
                              variant={getStatusVariant(job.status)}
                              className={
                                job.status === "active"
                                  ? "bg-green-500 hover:bg-green-600"
                                  : ""
                              }
                            >
                              {job.status === "active" ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {job.description}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleJobStatus(job.id)}
                          >
                            {job.status === "active" ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => runJobNow(job)}
                            disabled={job.isRunning}
                            className="flex items-center gap-1"
                          >
                            {job.isRunning ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />
                                Running...
                              </>
                            ) : (
                              <>
                                <Play size={16} />
                                Run Now
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Job Execution History</CardTitle>
              <CardDescription>
                View logs and results of previous job executions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job Name</TableHead>
                        <TableHead>Start Time</TableHead>
                        <TableHead>End Time</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Skeleton className="h-5 w-40" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-5 w-32" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-5 w-32" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-6 w-20" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-9 w-24" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <Select
                      value={selectedJob ? selectedJob.id : ""}
                      onValueChange={(value) => {
                        const job = jobs.find((j) => j.id === value);
                        if (job) setSelectedJob(job);
                        setCurrentPage(1);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by job" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobs.map((job) => (
                          <SelectItem key={job.id} value={job.id}>
                            {job.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {currentHistoryItems.length === 0 ? (
                    <div className="text-center p-8 text-gray-500">
                      No job history found
                    </div>
                  ) : (
                    <>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Job Name</TableHead>
                              <TableHead>Start Time</TableHead>
                              <TableHead>End Time</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {currentHistoryItems.map((history) => (
                              <TableRow key={history.id}>
                                <TableCell className="font-medium">
                                  {history.jobName}
                                </TableCell>
                                <TableCell>
                                  {formatDate(history.startTime)}
                                </TableCell>
                                <TableCell>
                                  {formatDate(history.endTime)}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={getStatusVariant(history.status)}
                                    className={`flex w-fit items-center gap-1 ${
                                      history.status?.toLowerCase() ===
                                      "success"
                                        ? "bg-green-500 hover:bg-green-600 text-white"
                                        : history.status?.toLowerCase() ===
                                          "running"
                                        ? ""
                                        : ""
                                    }`}
                                  >
                                    {selectedJobHistory &&
                                    selectedJobHistory.status?.toLowerCase() ===
                                      "success" ? (
                                      <CheckCircle size={12} />
                                    ) : selectedJobHistory &&
                                      selectedJobHistory.status?.toLowerCase() ===
                                        "running" ? (
                                      <Loader2
                                        size={12}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <XCircle size={12} />
                                    )}
                                    <span className="capitalize">
                                      {history.status}
                                    </span>
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      viewJobHistoryDetails(history)
                                    }
                                  >
                                    View Logs
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">
                            Rows per page:
                          </span>
                          <Select
                            value={rowsPerPage.toString()}
                            onValueChange={(value) => {
                              setRowsPerPage(Number(value));
                              setCurrentPage(1);
                            }}
                          >
                            <SelectTrigger className="w-[80px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="5">5</SelectItem>
                              <SelectItem value="10">10</SelectItem>
                              <SelectItem value="20">20</SelectItem>
                              <SelectItem value="50">50</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <EnhancedPaginationControl
                          totalPages={totalPages}
                          currentPage={currentPage - 1} // Convert to 0-based index
                          onPageChange={(page) => setCurrentPage(page + 1)} // Convert back to 1-based index
                        />
                        <div className="w-[160px]" />{" "}
                        {/* Spacer to balance layout */}
                      </div>
                    </>
                  )}
                </>
              )}

              {selectedJobHistory && (
                <Dialog
                  open={!!selectedJobHistory}
                  onOpenChange={() => setSelectedJobHistory(null)}
                >
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Job Execution Details</DialogTitle>
                      <DialogDescription>
                        {selectedJobHistory.jobName} -{" "}
                        {formatDate(selectedJobHistory.startTime)}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-sm text-gray-500">
                            Start Time
                          </h4>
                          <p>{formatDate(selectedJobHistory.startTime)}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-500">
                            End Time
                          </h4>
                          <p>{formatDate(selectedJobHistory.endTime)}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-500">
                            Status
                          </h4>
                          <Badge
                            variant={getStatusVariant(
                              selectedJobHistory.status
                            )}
                            className={`mt-1 flex w-fit items-center gap-1 ${
                              selectedJobHistory &&
                              selectedJobHistory.status?.toLowerCase() ===
                                "success"
                                ? "bg-green-500 hover:bg-green-600"
                                : ""
                            }`}
                          >
                            {selectedJobHistory &&
                            selectedJobHistory.status?.toLowerCase() ===
                              "success" ? (
                              <CheckCircle size={12} />
                            ) : selectedJobHistory &&
                              selectedJobHistory.status?.toLowerCase() ===
                                "running" ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <XCircle size={12} />
                            )}
                            <span className="capitalize">
                              {selectedJobHistory.status}
                            </span>
                          </Badge>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm text-gray-500">
                            Duration
                          </h4>
                          <p>
                            {selectedJobHistory.endTime
                              ? `${Math.round(
                                  (new Date(
                                    selectedJobHistory.endTime
                                  ).getTime() -
                                    new Date(
                                      selectedJobHistory.startTime
                                    ).getTime()) /
                                    1000
                                )} seconds`
                              : "Running..."}
                          </p>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-semibold text-sm text-gray-500 mb-2">
                          Logs
                        </h4>
                        <pre className="bg-gray-50 rounded-md p-4 text-sm overflow-auto max-h-60 whitespace-pre-wrap">
                          {selectedJobHistory.logs}
                        </pre>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setSelectedJobHistory(null)}
                      >
                        Close
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Jobs;
